/*
Attribution: https://jasonwatmore.com/post/2020/04/11/react-hooks-bootstrap-alert-notifications

The alert service acts as the bridge between any component in an React application and the alert component that actually displays the alert notification. It contains methods for sending, clearing and subscribing to alerts.

The AlertType object defines the types of alerts allowed in the application.

You can trigger alert notifications from any component in the application by calling one of the convenience methods for displaying different types of alerts: success(), error(), info() and warn().

Alert convenience method parameters
The first parameter is the alert message string, which can be plain text or HTML
The second parameter is an optional options object that supports an autoClose boolean property and keepAfterRouteChange boolean property:
autoClose - if true tells the alert component to automatically close the alert after five seconds. Default is true.
keepAfterRouteChange - if true prevents the alert from being closed after one route change, this is handy for displaying messages after a redirect such as when a new record is created or updated. Default is false.
*/

// Dependencies
//import { Subject } from 'rxjs';
import { BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";

//const alertSubject = new Subject();

export interface AlertOptions {
  id?: string;
  type: string;
  message: string;
  autoClose?: boolean;
  keepAfterRouteChange?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
}

interface Alert extends AlertOptions {
  id: string;
  autoClose: boolean;
  keepAfterRouteChange: boolean;
  autoFocus: boolean;
}

const alertSubject = new BehaviorSubject<Alert | null>(null);
const defaultId = "default-alert";

export const alertService = {
  onAlert,
  success,
  error,
  info,
  warn,
  alert,
  clear,
};

export const AlertType = {
  Success: "Success",
  Error: "Error",
  Info: "Info",
  Warning: "Warning",
};

// enable subscribing to alerts observable
function onAlert(id: string = defaultId) {
  return alertSubject
    .asObservable()
    .pipe(filter((x): x is Alert => x !== null && x.id === id));
}

// convenience methods
function success(message: string, options?: Partial<AlertOptions>) {
  alert({ ...options, type: AlertType.Success, message });
}

function error(message: string, options?: Partial<AlertOptions>) {
  alert({ ...options, type: AlertType.Error, message });
}

function info(message: string, options?: Partial<AlertOptions>) {
  alert({ ...options, type: AlertType.Info, message });
}

function warn(message: string, options?: Partial<AlertOptions>) {
  alert({ ...options, type: AlertType.Warning, message });
}

// core alert method
function alert(alertOptions: AlertOptions) {
  const alert: Alert = {
    id: alertOptions.id || defaultId,
    type: alertOptions.type,
    message: alertOptions.message,
    autoClose:
      alertOptions.autoClose === undefined ? true : alertOptions.autoClose,
    keepAfterRouteChange: alertOptions.keepAfterRouteChange || false,
    // Default autoFocus to true for error alerts unless explicitly set
    autoFocus:
      alertOptions.autoFocus === undefined
        ? alertOptions.type === AlertType.Error
        : alertOptions.autoFocus,
    onClose: alertOptions.onClose,
  };
  alertSubject.next(alert);
}

// clear alerts
function clear(id: string = defaultId) {
  alertSubject.next({
    id,
    type: "",
    message: "",
    autoClose: false,
    keepAfterRouteChange: false,
    autoFocus: false,
  });
}
